import http from 'api/httpClient';

const BASE = '/api/v1/contracts';

// ---------- types ----------
export type Option = { code: string; name: string };

export type ContractListItem = {
  id: number;
  contract_number: string | null;
  customer_code: string;
  customer_name: string | null;
  customer_division: string | null;
  customer_location: string | null;
  material_type: string | null;      // of the current version
  status: string;
  start_date: string | null;
  end_date: string | null;
  current_version_number: string | null;
  created_by: string | null;
  created_at: string | null;
  job_id: string | null;   // latest processing job — poll GET /jobs/{job_id}
  job_status: string | null;
};

export type VersionSummary = {
  id: number;
  version_number: string;
  version_type: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  rate_type: string | null;
  material_type: string | null;
  material_value_per_kg: number | null;
  packaging_modes: string[];          // filled for the current version
  packaging_other: string | null;
  uploaded_at: string | null;
};

export type KycInfo = {
  pan: string | null;
  pan_status: string | null;
  registered_name: string | null;
  entity_type: string | null;
  full_address: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  country: string | null;
  email_masked: string | null;
  mobile_masked: string | null;
};

export type GstinItem = {
  id: number;
  gstin: string;
  gst_status: string | null;
  gst_state: string | null;
  is_selected_for_billing: boolean;
};

export type ContactItem = {
  id?: number;
  contact_type: string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  designation?: string | null;
};

export type RateItem = {
  id?: number;
  origin_text?: string | null;
  destination_text?: string | null;
  vehicle_type?: string | null;
  rate?: number | null;
  rate_unit?: string | null;
  rate_type?: string | null;
  minimum_charge?: number | null;
  detention_charge?: number | null;
  effective_from?: string | null;
  effective_to?: string | null;
  mapping_status: string;
  source_document_id?: number | null;   // the rate sheet the row came from
  source_page?: number | null;
  change_kind?: 'changed' | 'added' | 'carried' | null;   // changes-only revision
  details?: Record<string, string>;   // extra rate-sheet columns (header -> value), read-only
};

// One rate card: revision 0 is the original upload's; later ones are rate revisions,
// read from new rate sheets into a draft that is reviewed and then applied.
export type RateSet = {
  id: number;
  revision_number: number;
  mode: 'full' | 'changes';
  status: 'processing' | 'draft' | 'live' | 'superseded' | 'discarded' | string;
  effective_from: string | null;
  remarks: string | null;
  created_by: string | null;
  created_at: string | null;
  applied_by: string | null;
  applied_at: string | null;
  rows_count: number;
  changed_count: number;
  added_count: number;
  job_id: string | null;
  job_status: string | null;
  job_error: string | null;
  editable: boolean;
};

export type FieldValue = {
  field_key: string;
  final_value: string | null;
  status: string;
  source_page: number | null;
  source_excerpt: string | null;
};

// where a missing item is filled in: the upload form, the Customer & KYC tab, or a catalog section
export type MissingItem = { label: string; section: 'upload' | 'customer' | 'terms' | 'billing_credit' | 'sla' | string };

export type DocumentItem = {
  id: number;
  document_type: string;
  file_name: string;
  mime_type: string | null;
  page_count: number | null;
  shared: boolean;   // the shared terms' main document, uploaded with another contract
  rate_set_id: number | null;                         // rate sheets: the revision they came with
  parse_status: 'ok' | 'no_rows' | 'failed' | null;   // rate sheets: could their rates be read
  parse_rows: number | null;
  parse_error: string | null;
};

// The terms (main document + Terms / Billing & Credit / SLA fields) a contract uses;
// several contracts of one customer can share them.
export type TermsInfo = {
  id: number;
  label: string;
  locked: boolean;       // a contract using these terms is active: fields are read-only
  processing: boolean;   // the contract that brought these terms is still extracting them
  shared_with: { id: number; contract_number: string | null; customer_division: string | null }[];
  shared_count: number;  // all other contracts using them (some may not be visible to you)
};

export type TermsOption = {
  id: number;
  label: string;
  created_at: string | null;
  contracts_count: number;
  locked: boolean;
};

export type ContractDetail = {
  id: number;
  contract_number: string | null;
  customer_code: string;
  customer_name: string | null;
  customer_division: string | null;
  customer_location: string | null;
  status: string;             // display status: the stored one, or 'expired' past the end date
  lifecycle_status: string;   // the stored status — what editing / activation follow
  job_id: string | null;
  job_status: string | null;
  job_error: string | null;   // set when the latest processing job failed
  missing_for_activation: MissingItem[];   // what still blocks activation (empty once active)
  can_edit_details: boolean;               // caller holds "Edit Contract Details" and it isn't processing
  org_unit_names: Record<string, string>;  // division / branch code -> name (empty if the org directory is down)
  user_names: Record<string, string>;      // access-user ecode -> full name
  divisions: string[];
  access_users: string[];
  booking_branches: string[];
  billing_branches: string[];
  current_version: VersionSummary | null;
  versions: VersionSummary[];
  documents: DocumentItem[];
  kyc: KycInfo | null;
  gstins: GstinItem[];
  contacts: ContactItem[];
  rates: RateItem[];                  // the rows of rate_set_id: the live rate card (revision 0 before activation)
  rate_set_id: number | null;
  rate_sets: RateSet[];               // every revision, newest first
  draft_revision: RateSet | null;     // the revision being read / awaiting review
  fields: FieldValue[];
  terms: TermsInfo | null;
};

export type JobResponse = {
  id: string;
  contract_version_id: number;
  status: string;
  error: string | null;
};

export type CatalogField = {
  key: string;
  label: string;
  section: string;
  data_type: string;
  options: string[];
  required_for_activation: boolean;
  help_text: string | null;
};
export type CatalogSection = { section: string; label: string; fields: CatalogField[] };
export type FieldCatalog = { sections: CatalogSection[] };

export type CreateContractResponse = {
  contract_id: number;
  version_id: number;
  job_id: string;
  contract_number: string;
};

// ---------- calls ----------
// org-directory levels + financial year used for the division / branch dropdowns
export const DIVISION_LEVEL = 5;   // org-dir level 5 = Division
export const BRANCH_LEVEL = 1;     // OU / branch level
export function currentFY(): string {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() + 1 >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

export async function listContracts(params: {
  search?: string; status?: string; limit?: number; offset?: number;
  bookingBranches?: string[]; billingBranches?: string[];   // match contracts with ANY of these
}) {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.status) q.set('status', params.status);
  (params.bookingBranches ?? []).forEach((b) => q.append('booking_branch', b));
  (params.billingBranches ?? []).forEach((b) => q.append('billing_branch', b));
  q.set('limit', String(params.limit ?? 50));
  q.set('offset', String(params.offset ?? 0));
  return http.get<{ contracts: ContractListItem[]; total: number }>(`${BASE}?${q.toString()}`);
}

export async function getContract(id: number) {
  return http.get<ContractDetail>(`${BASE}/${id}`);
}

export async function getJob(jobId: string) {
  return http.get<JobResponse>(`${BASE}/jobs/${jobId}`);
}

// job statuses that mean the run has finished (the contract is now needs_completion)
export const TERMINAL_JOB_STATUSES = ['ready_for_user_completion', 'completed', 'failed'];
export const isTerminalJob = (s: string | null | undefined) =>
  !!s && TERMINAL_JOB_STATUSES.includes(s);

export async function getFieldCatalog() {
  return http.get<FieldCatalog>(`${BASE}/field-catalog`);
}

export async function customerOptions(search?: string) {
  const q = search ? `?search=${encodeURIComponent(search)}` : '';
  return http.get<{ options: Option[] }>(`${BASE}/options/customers${q}`);
}
export async function userOptions() {
  return http.get<{ options: Option[] }>(`${BASE}/options/users`);
}
export async function ouOptions(levelId: number, year: string) {
  return http.get<{ options: Option[] }>(`${BASE}/options/ous?level_id=${levelId}&year=${encodeURIComponent(year)}`);
}

export async function customerTerms(customerCode: string) {
  return http.get<{ terms: TermsOption[] }>(`${BASE}/options/terms?customer_code=${encodeURIComponent(customerCode)}`);
}

export async function createContract(form: FormData) {
  return http.post<CreateContractResponse>(BASE, form);
}

// Everything on the upload form except the customer and the files.
export type ContractDetailsUpdate = {
  customer_division: string | null;
  customer_location: string | null;
  divisions: string[];
  access_users: string[];
  booking_branches: string[];
  billing_branches: string[];
  start_date: string;
  end_date: string;
  rate_type: string;
  material_type: string;
  material_value_per_kg: number | null;
  packaging_modes: string[];
  packaging_other: string | null;
};
export async function updateContractDetails(contractId: number, body: ContractDetailsUpdate) {
  return http.put(`${BASE}/${contractId}/details`, body);
}
export async function patchFields(versionId: number, fields: { field_key: string; final_value: string | null }[]) {
  return http.patch(`${BASE}/versions/${versionId}/fields`, { fields });
}
export async function selectGstin(versionId: number, gstinId: number) {
  return http.patch(`${BASE}/versions/${versionId}/gstin/select`, { gstin_id: gstinId });
}
export async function putRates(rateSetId: number, rates: RateItem[]) {
  return http.put(`${BASE}/rate-sets/${rateSetId}/rates`, { rates });
}
export async function getRateSetRates(rateSetId: number) {
  return http.get<{ rate_set: RateSet; rates: RateItem[] }>(`${BASE}/rate-sets/${rateSetId}/rates`);
}
// rate_sheets[], mode (full | changes), effective_from?, remarks?
export async function uploadRateRevision(contractId: number, form: FormData) {
  return http.post<{ rate_set_id: number; revision_number: number; job_id: string }>(`${BASE}/${contractId}/rate-revisions`, form);
}
export async function applyRateRevision(rateSetId: number) {
  return http.post(`${BASE}/rate-sets/${rateSetId}/apply`, {});
}
export async function discardRateRevision(rateSetId: number) {
  return http.post(`${BASE}/rate-sets/${rateSetId}/discard`, {});
}
export async function putContacts(versionId: number, contacts: ContactItem[]) {
  return http.put(`${BASE}/versions/${versionId}/contacts`, { contacts });
}
export async function reprocessVersion(versionId: number) {
  return http.post(`${BASE}/versions/${versionId}/reprocess`, {});
}
export async function activateContract(contractId: number, versionId: number) {
  return http.post(`${BASE}/${contractId}/versions/${versionId}/activate`, {});
}

// ---------- documents ----------
const DOCUMENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
};

export const documentExt = (fileName: string) => (fileName.split('.').pop() || '').toLowerCase();

// The served bytes of a stored document, typed from its extension (the same allowlist as the API).
export async function fetchDocumentBlob(contractId: number, doc: DocumentItem): Promise<Blob> {
  const blob = await http.get<Blob>(`${BASE}/${contractId}/documents/${doc.id}/file`, { responseType: 'blob' });
  return new Blob([blob], { type: DOCUMENT_TYPES[documentExt(doc.file_name)] || 'application/octet-stream' });
}

export function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);  // revoking synchronously can cancel the download
}

export const PACKAGING_MODES = [
  'Carton', 'Bag / Sack', 'Pallet', 'Wooden Box', 'Wooden Crate', 'Drum',
  'Bundle', 'Roll', 'Coil', 'Tank', 'Loose Cargo', 'Machine / Equipment',
  'Skid', 'Container', 'Other',
];
export const RATE_TYPES = ['Daily', 'Spot', 'Project Based', 'Monthly', 'Quarterly', 'Annual'];
